/**
 * Auto Coupon — copy code, FAQ modal, percentage recalc + visibility on variant change.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    const format = window.themeVariables?.settings?.moneyFormat;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && format) {
      return Shopify.formatMoney(cents, format);
    }
    return '$' + Math.floor(cents / 100);
  }

  function formatMoneyWithoutTrailingZeros(cents) {
    const formatted = formatMoney(cents);
    return formatted.replace(/(\.|,)00(?=\D*$)/, '');
  }

  function calcSaveCents(variantPriceCents, root) {
    const type = root.dataset.discountType;

    if (type === 'percentage') {
      const percent = parseInt(root.dataset.discountPercent || '0', 10);
      if (!percent || !variantPriceCents) return 0;
      const saveDollars = Math.floor((variantPriceCents * percent) / 100 / 100);
      return saveDollars * 100;
    }

    const fixedDollars = parseInt(root.dataset.discountAmount || '0', 10);
    return fixedDollars * 100;
  }

  function stripStubTitles(root) {
    const stub = root.querySelector('.product-auto-coupon__stub');
    if (!stub) return;

    stub.removeAttribute('title');
    stub.querySelectorAll('[title]').forEach((el) => {
      el.removeAttribute('title');
    });
  }

  function watchStubTitles(root) {
    const stub = root.querySelector('.product-auto-coupon__stub');
    if (!stub || stub.dataset.titleWatchBound) return;

    stub.dataset.titleWatchBound = 'true';
    stripStubTitles(root);

    const observer = new MutationObserver(() => {
      stripStubTitles(root);
    });

    observer.observe(stub, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['title']
    });
  }

  function updateAmounts(root, cents) {
    if (!cents || cents <= 0) return;
    const formatted = formatMoneyWithoutTrailingZeros(cents);
    root.querySelectorAll('[data-auto-coupon-amount], [data-auto-coupon-amount-inline]').forEach((el) => {
      el.textContent = formatted;
    });
    stripStubTitles(root);
  }

  function copyCode(button, root) {
    const code = root.dataset.couponCode;
    if (!code) return;

    const copiedLabel = root.dataset.copiedLabel || 'Copied!';
    const defaultLabel = button.dataset.defaultLabel || button.textContent;

    const onSuccess = () => {
      button.classList.add('is-copied');
      button.textContent = copiedLabel;
      window.setTimeout(() => {
        button.classList.remove('is-copied');
        button.textContent = defaultLabel;
      }, 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(onSuccess).catch(() => {
        fallbackCopy(code, onSuccess);
      });
    } else {
      fallbackCopy(code, onSuccess);
    }
  }

  function fallbackCopy(text, onSuccess) {
    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'absolute';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();
    try {
      document.execCommand('copy');
      onSuccess();
    } catch (e) {
      /* ignore */
    }
    document.body.removeChild(input);
  }

  function openFaqModal(dialog, trigger) {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function closeFaqModal(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    const trigger = document.querySelector('[aria-controls="' + dialog.id + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function initFaq(root) {
    const trigger = root.querySelector('[data-auto-coupon-faq-trigger]');
    const dialog = root.querySelector('.product-auto-coupon-faq');
    if (!trigger || !dialog || trigger.dataset.faqBound) return;

    trigger.dataset.faqBound = 'true';

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openFaqModal(dialog, trigger);
    });

    dialog.querySelectorAll('[data-auto-coupon-faq-close]').forEach((btn) => {
      btn.addEventListener('click', () => closeFaqModal(dialog));
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeFaqModal(dialog);
    });

    dialog.addEventListener('close', () => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function initAutoCoupon(root) {
    if (!root || root.dataset.autoCouponBound) return;
    root.dataset.autoCouponBound = 'true';

    const copyBtn = root.querySelector('[data-auto-coupon-copy]');
    if (copyBtn) {
      copyBtn.dataset.defaultLabel = copyBtn.textContent.trim();
      copyBtn.addEventListener('click', () => copyCode(copyBtn, root));
    }

    initFaq(root);
    watchStubTitles(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.product-auto-coupon').forEach((root) => {
      root.dataset.autoCouponBound = '';
      initAutoCoupon(root);
    });
  }

  function getActiveVariant() {
    const script = document.querySelector('variant-picker script[data-variant]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function syncPromosVisibility(variant) {
    if (!variant || variant.price == null) return;

    document.querySelectorAll('[data-block-type="auto-coupon"]').forEach((block) => {
      const coupon = block.querySelector('.product-auto-coupon');
      if (!coupon) return;

      const minimumCents = parseInt(coupon.dataset.minimumPurchaseCents || '0', 10);
      block.hidden = minimumCents > 0 && variant.price < minimumCents;
    });
  }

  function onVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant || variant.price == null) return;

    syncPromosVisibility(variant);

    document.querySelectorAll('.product-auto-coupon[data-discount-type="percentage"]').forEach((root) => {
      const cents = calcSaveCents(variant.price, root);
      updateAmounts(root, cents);
    });
  }

  function onPromosRerender() {
    initAll(document);
    syncPromosVisibility(getActiveVariant());
  }

  document.addEventListener('DOMContentLoaded', () => {
    initAll(document);
    syncPromosVisibility(getActiveVariant());
  });
  document.addEventListener('variant:change', onVariantChange);
  document.addEventListener('product:rerender', onPromosRerender);
})();
